// EditBanking.jsx
import { t } from 'i18next';
import React, { useState, useEffect } from 'react';
import styles from './EditBanking.module.css';
import { v4 as uuidv4 } from 'uuid';  // 고유 ID 생성용
import { STOCK_TYPES } from '../common/stockType';

export default function EditBanking(props) {
    const { selectedDraft, onClose } = props;
    const { bundleId, tempQuery } = selectedDraft;
    const sharpToHash = str => str ? str.replace('#', 'hashmark') : str;

    // ── tempQuery 파싱 헬퍼 ──
    //   [2026-10-01] 따옴표 없는 NULL 은 빈값으로 읽는다. 예전에는 글자 'NULL' 로 읽혀
    //   수정 기안에 문자열 'NULL' 이 들어갔다(Banking_Info.vialNumbers 'NULL' 의 출처).
    const unquote = (tok) => (/^null$/i.test(tok) ? '' : tok.replace(/^'+|'+$/g, ''));
    const parseTempQuery = (q = '') => {
        const updates = {};
        if (!q) return updates;
        const normalized = q.replace(/\s+/g, ' ').trim();

        // INSERT ... VALUES(...)
        if (/insert\s+into/i.test(normalized) && /values/i.test(normalized)) {
            const m = normalized.match(
                /insert\s+into\s+[`"]?(?:\w+\.)?`?[\w`"]+\s*\(([^)]+)\)\s*values\s*\(([\s\S]+)\)/i
            );
            if (m) {
                const keys = m[1].split(',').map(k => k.trim().replace(/[`'"]/g, ''));
                const raw = m[2].trim();
                const vals = [];
                let cur = '', inQ = false, depth = 0;
                for (let ch of raw) {
                    if (ch === "'" && !inQ) { inQ = true; cur += ch; }
                    else if (ch === "'" && inQ) { inQ = false; cur += ch; }
                    else if (!inQ && ch === '(') { depth++; cur += ch; }
                    else if (!inQ && ch === ')') { depth--; cur += ch; }
                    else if (!inQ && ch === ',' && depth === 0) {
                        vals.push(unquote(cur.trim()));
                        cur = '';
                    } else cur += ch;
                }
                if (cur) vals.push(unquote(cur.trim()));
                keys.forEach((k, i) => { updates[k] = vals[i] ?? ''; });
            }
        }
        // UPDATE ... SET ... WHERE ...
        else if (/update\s+/i.test(normalized) && /set\s+/i.test(normalized)) {
            const m = normalized.match(/set\s+(.+?)\s+where/i);
            if (m) {
                m[1]
                    .split(/,(?=(?:[^']*'[^']*')*[^']*$)/)
                    .forEach(pair => {
                        const [rk, rv] = pair.split('=');
                        const key = rk.trim().replace(/['`"]/g, '');
                        const val = rv.trim().replace(/['`"]/g, '');
                        updates[key] = val;
                    });
            }
        }
        return updates;
    };

    // 단일 기안에서 공통으로 뽑아낼 필드들
    const draftData = parseTempQuery(tempQuery);

    // ── 폼 상태 초기화 ──
    const [formData, setFormData] = useState({
        time: draftData.time || '',
        patientCode: draftData.PatientCode || '',
        master: draftData.Master || '0',
        organoidInfoKey: (draftData.Organoid_Info_Key || '').replace('hashmark', '#'),
        labCode: draftData.LabCode || '',
        passageNo: draftData.PassageNo || '',
        keeper: draftData.Keeper || '',
        freezingMedia: draftData.FreezingMedia || '',
        freezingDate: (draftData.FreezingDate || '').split(' ')[0],
        domePerVialOrCellNumber: draftData.domePerVialOrCellNumber || '',
        cultureMedia: draftData.CultureMedia || '',
        mycoplasma: draftData.Mycoplasma || '',
        thawingNo: draftData.thawingNo || '',
        annotation: draftData.annotation || '',
        exporter: draftData.Exporter || '',
        exportDate: (draftData.ExporteDate || '').split(' ')[0],
        vialNumbers: draftData.vialNumbers || '',
        // [2026-10-01] 자스탁 기안의 부모. 예전에는 폼에 옮겨 담지 않아 수정하면 일반 스탁이 됐다.
        //   서버(draftAddBankingInfo)는 form.ParentCode 가 있으면 "<구분> Child Stock import" 로 등록한다.
        ParentCode: draftData.ParentCode || '',
        vessels: []  // bundleId 유무에 따라 채워질 예정
    });

    // ── bundleId 있을 때 기안들에서 위치 정보만 뽑아 vessels에 세팅 ──
    useEffect(() => {
        if (bundleId) {
            fetch('/api/getDraftByBundleId', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ bundleId })
            })
                .then(res => res.json())
                .then(drafts => {
                    const vesselGroups = drafts.map(d => {
                        const u = parseTempQuery(d.tempQuery);
                        return {
                            vessel: u.Vessel || '',
                            line: u.Line || '',
                            box: u.Box || '',
                            position: u.Position || ''
                        };
                    });
                    setFormData(fd => ({ ...fd, vessels: vesselGroups }));
                })
                .catch(console.error);
        } else {
            // 단일 기안일 때는 최초 draftData 기준으로 하나만 세팅
            setFormData(fd => ({
                ...fd,
                vessels: [{
                    vessel: draftData.Vessel || '',
                    line: draftData.Line || '',
                    box: draftData.Box || '',
                    position: draftData.Position || ''
                }]
            }));
        }
    }, [bundleId, tempQuery]);

    // ── 공통 데이터 로드 (라인 옵션, 사용된 위치) ──
    const [lineOptions, setLineOptions] = useState([]);
    const [occupiedPositions, setOccupiedPositions] = useState([]);
    useEffect(() => {
        fetch('/api/getLineList', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
            .then(r => r.json()).then(setLineOptions).catch(console.error);
        fetch('/api/getOccupiedPositions', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
            .then(r => r.json()).then(setOccupiedPositions).catch(console.error);
    }, []);

    // ── 핸들러들 ──
    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(fd => ({ ...fd, [name]: value }));
    };
    // [2026-09-09] Master 체크박스 → MCB/WCB/ARB 3지선다
    const handleMasterChange = e => {
        setFormData(fd => ({ ...fd, master: e.target.value }));
    };
    const handleVesselChange = (idx, e) => {
        const { name, value } = e.target;
        setFormData(fd => {
            const vs = [...fd.vessels];
            vs[idx][name] = value;
            if (name === 'vessel') vs[idx].line = '';
            return { ...fd, vessels: vs };
        });
    };
    const assignPosition = idx => {
        const v = formData.vessels[idx];
        if (!v.vessel || !v.line || !v.box) {
            alert(t('먼저 Vessel, Line, Box를 선택해주세요.'));
            return;
        }
        const takenDB = occupiedPositions
            .filter(p => String(p.Vessel) === v.vessel && p.Line === v.line && p.Box === v.box)
            .map(p => Number(p.Position));
        const takenForm = formData.vessels
            .filter((_, i) => i !== idx)
            .map(x => Number(x.position) || 0);
        const taken = new Set([...takenDB, ...takenForm]);
        let av = 1; while (av <= 100 && taken.has(av)) av++;
        if (av > 100) {
            alert(t('사용 가능한 Position이 없습니다.'));
            return;
        }
        handleVesselChange(idx, { target: { name: 'position', value: String(av) } });
    };
    const addVesselGroup = () => {
        setFormData(fd => ({
            ...fd,
            vessels: [...fd.vessels, { vessel: '', line: '', box: '', position: '' }]
        }));
    };
    const removeVesselGroup = idx => {
        setFormData(fd => {
            const vs = [...fd.vessels]; vs.splice(idx, 1);
            return { ...fd, vessels: vs };
        });
    };

    // ── 제출 핸들러 ──
    const handleSubmitClick = async () => {
        // (중복 검사 생략)
        const needBundle = formData.vessels.length > 1;
        const bundleParam = needBundle ? { bundleId: uuidv4() } : {};
        const orgKey = sharpToHash(formData.organoidInfoKey);
        const messages = [];
        let sawConflict = false;

        for (const v of formData.vessels) {
            const param = {
                ...formData,
                organoidInfoKey: orgKey,
                vessels: [v],
                ...bundleParam
            };
            try {
                const res = await fetch('/api/draftAddBankingInfo', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ param })
                });
                const data = await res.json();
                if (res.status === 409) {
                    sawConflict = true;
                    messages.push(`⚠️ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message}`);
                } else if (!res.ok) {
                    messages.push(`❌ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message || t('서버 오류')}`);
                } else {
                    messages.push(`✅ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message}`);
                }
            } catch {
                messages.push(`⚠️ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${t('요청 실패')}`);
            }
        }

        // [2026-10-01] 수정은 새 기안을 만드는 방식이다. 원래 기안이 대기중으로 남으면
        //   둘 다 승인돼 같은 바이알이 두 번 들어갈 수 있어서, 새 기안이 전부 등록됐을 때만 원래 기안을 취소한다.
        //   /api/cancelDraft 는 번들이면 번들 전체를, 본인 기안만 취소한다.
        const allOk = messages.length > 0 && messages.every(m => m.startsWith('✅'));
        if (allOk && selectedDraft.authorizedStatus === 'pending') {
            try {
                const res = await fetch('/api/cancelDraft', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ draftDocumentId: selectedDraft.draftDocumentId, bundleId: selectedDraft.bundleId || null })
                });
                const data = await res.json();
                messages.push(data.message === '기안을 취소하였습니다.'
                    ? t('기존 기안은 자동으로 취소되었습니다.')
                    : `⚠️ ${t('기존 기안이 취소되지 않았습니다. 직접 취소(또는 반려)해 주세요.')} (${data.message})`);
            } catch {
                messages.push(`⚠️ ${t('기존 기안이 취소되지 않았습니다. 직접 취소(또는 반려)해 주세요.')}`);
            }
        } else if (!allOk) {
            messages.push(`⚠️ ${t('일부 기안이 등록되지 않아 기존 기안은 취소하지 않았습니다.')}`);
        }

        alert(messages.join('\n'));
        if (!sawConflict) onClose();
    };

    return (
        <div className={styles.modalBackground}>
            <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
                <button type="button" className={styles.closeButton} onClick={props.onClose}>&times;</button>
                <div className={styles.title}>{t('Edit Organoid Banking(Stock)')}</div>

                <div className={styles.submitGroup}>
                    <div className={styles.formLabel}>Stock</div>
                    <select
                        name="master"
                        value={formData.master}
                        onChange={handleMasterChange}
                        className={styles.rightValue}
                    >
                        {STOCK_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.full}</option>
                        ))}
                    </select>

                    <div className={styles.formLabel}>Organoid Code</div>
                    <input
                        type="text"
                        name="organoidInfoKey"
                        value={formData.organoidInfoKey}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Lab Code</div>
                    <input
                        type="text"
                        name="labCode"
                        value={formData.labCode}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Passage No</div>
                    <input
                        type="text"
                        name="passageNo"
                        value={formData.passageNo}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Keeper</div>
                    <input
                        type="text"
                        name="keeper"
                        value={formData.keeper}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Freezing Media</div>
                    <input
                        list="freezingMediaList"
                        name="freezingMedia"
                        value={formData.freezingMedia}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />
                    <datalist id="freezingMediaList">
                        <option value="Recovery Cell Culture Freezing Medium" />
                    </datalist>

                    <div className={styles.formLabel}>Freezing Date</div>
                    <input
                        type="date"
                        name="freezingDate"
                        value={formData.freezingDate}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Dome Per Vial/Cell Number</div>
                    <input
                        type="text"
                        name="domePerVialOrCellNumber"
                        value={formData.domePerVialOrCellNumber}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Culture Media</div>
                    <input
                        type="text"
                        name="cultureMedia"
                        value={formData.cultureMedia}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Mycoplasma</div>
                    <select
                        name="mycoplasma"
                        value={formData.mycoplasma}
                        onChange={handleChange}
                        className={styles.rightValue}
                    >
                        <option value="" />
                        <option value="NA">NA</option>
                        <option value="Negative">Negative</option>
                        <option value="Positive">Positive</option>
                    </select>

                    <div className={styles.formLabel}>Thawing No</div>
                    <input
                        type="text"
                        name="thawingNo"
                        value={formData.thawingNo}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Annotation</div>
                    <textarea
                        name="annotation"
                        value={formData.annotation}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Exporter</div>
                    <input
                        type="text"
                        name="exporter"
                        value={formData.exporter}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Export Date</div>
                    <input
                        type="date"
                        name="exportDate"
                        value={formData.exportDate}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />
                </div>

                {formData.vessels.map((v, idx) => (
                    <div className={styles.positionBox} key={idx}>
                        <span className={styles.positionNumber}>{idx + 1}</span>
                        <div className={styles.vesselGroup}>
                            <div className={styles.formLabel}>Vessel</div>
                            <input
                                type="number"
                                name="vessel"
                                placeholder={t('1~4 사이 숫자를 입력해주세요.')}
                                value={v.vessel ?? ''}
                                onChange={e => {
                                    const val = e.target.value;
                                    if (
                                        val === '' ||
                                        (/^[1-4]$/.test(val) && Number(val) >= 1 && Number(val) <= 4)
                                    ) handleVesselChange(idx, e);
                                }}
                                min="1" max="4" step="1"
                                className={styles.rightValue}
                            />

                            <div className={styles.formLabel}>Line</div>
                            <select
                                name="line"
                                value={v.line}
                                onChange={e => handleVesselChange(idx, e)}
                                className={styles.rightValue}
                            >
                                <option value="" disabled>{t('라인을 선택해주세요.')}</option>
                                {lineOptions
                                    .filter(opt => String(opt.vesselNo) === v.vessel)
                                    .map(opt => (
                                        <option key={opt.lineName} value={opt.lineName}>
                                            {opt.lineName}
                                        </option>
                                    ))
                                }
                            </select>

                            <div className={styles.formLabel}>Box</div>
                            <select
                                name="box"
                                value={v.box}
                                onChange={e => handleVesselChange(idx, e)}
                                required
                                className={styles.rightValue}
                            >
                                <option value="" disabled>{t('BOX1에서 BOX13 중에 선택해주세요.')}</option>
                                <option value="" />
                                {Array.from({ length: 13 }, (_, i) => (
                                    <option key={i} value={`BOX${i + 1}`}>{`BOX${i + 1}`}</option>
                                ))}
                            </select>

                            <div className={styles.formLabel}>Position</div>
                            <input
                                type="number"
                                name="position"
                                value={v.position ?? ''}
                                min="0" max="100" step="1"
                                placeholder={t('1에서 100사이의 숫자를 입력해주세요.')}
                                className={styles.rightValue}
                                onInput={e => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                        handleVesselChange(idx, e);
                                        return;
                                    }
                                    const n = Number(raw);
                                    if (0 <= n && n <= 100) {
                                        handleVesselChange(idx, e);
                                    } else {
                                        e.target.value = raw.slice(0, -1);
                                        handleVesselChange(idx, { target: e.target });
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => assignPosition(idx)}
                                className={styles.autoButton}
                            >
                                {t('자동할당')}
                            </button>

                            <button
                                type="button"
                                onClick={() => removeVesselGroup(idx)}
                                className={styles.removeButton}
                            >-</button>
                        </div>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={addVesselGroup}
                    className={styles.addButton}
                >+</button>

                <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t('Draft your opinion here.')}
                    className={styles.draftOpinion}
                />

                <div className={styles.submitDiv}>
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={handleSubmitClick}
                    >{t('Submit')}</button>
                </div>
            </div>
        </div>
    );
}
